import styled, { css } from "styled-components";
import { designSystemVars as vars } from "../tokens";
import { FormField, FormValue } from "./Form";

export const PageColumns = styled.div`
  display: flex;
  width: 100%;
  box-sizing: border-box;
  flex-direction: row;
  align-items: flex-start;
  gap: ${vars.spacing["3xl"]};
  padding-top: var(--inzeloop-ds-spacing-20px);
  padding-bottom: var(--inzeloop-ds-spacing-30px);
`;

export const PreviewContentColumn = styled.div<{
  $isCollapsed?: boolean;
  $fluid?: boolean;
}>`
  display: flex;
  box-sizing: border-box;
  flex-shrink: 0;
  flex-direction: column;
  align-items: flex-start;
  gap: ${vars.spacing.sm};

  ${({ $isCollapsed, $fluid }) =>
    $fluid
      ? css`
          width: 100%;
          min-width: 0;
          max-width: none;
          flex: 1 1 auto;
          align-items: stretch;
        `
      : css`
          @media (min-width: 834px) {
            width: ${$isCollapsed ? "659px" : "510px"};
          }

          @media (min-width: 1280px) {
            width: ${$isCollapsed ? "1108px" : "966px"};
          }

          @media (min-width: 1680px) {
            width: 981px;
          }
        `}

  &:focus-visible {
    outline: none;
    border-radius: ${vars.radius.sm};
    box-shadow: ${vars.shadow.focus};
  }
`;

export const PageContentColumn = styled.div<{ $isCollapsed?: boolean }>`
  display: flex;
  min-width: 0;
  box-sizing: border-box;
  flex: 1;
  flex-shrink: 0;
  flex-direction: column;
  align-items: flex-start;
  gap: ${vars.spacing.sm};

  &:focus-visible {
    outline: none;
    border-radius: ${vars.radius.sm};
    box-shadow: ${vars.shadow.focus};
  }
`;

export const StickyPageTitleRow = styled.div`
  position: sticky;
  top: 0;
  z-index: 100;
  display: flex;
  width: 100%;
  box-sizing: border-box;
  align-items: center;
  justify-content: space-between;
  padding-bottom: ${vars.spacing.sm};
  background: ${vars.color.background.page};
`;

export const PageSectionTitle = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  color: ${vars.color.text.muted};
  font-family: ${vars.typography.family.sans};
  font-size: ${vars.typography.size.lg};
  font-weight: ${vars.typography.weight.semibold};
  line-height: ${vars.typography.lineHeight.lg};
`;

export const InlineProcessing = styled.div`
  display: flex;
  align-items: center;
  gap: ${vars.spacing.sm};
`;

export const FilterToolbar = styled.div`
  display: flex;
  width: 100%;
  height: 68px;
  box-sizing: border-box;
  align-items: center;
  justify-content: space-between;
  padding-top: ${vars.spacing.lg};
  padding-bottom: ${vars.spacing.lg};
`;

export const FilterTag = styled.div`
  display: flex;
  height: 32px;
  align-items: center;
  justify-content: center;
  gap: ${vars.spacing.xs};
  padding: ${vars.spacing.xs} ${vars.spacing.sm};
  border-radius: ${vars.radius.sm};
  background: var(--inzeloop-ds-color-background-selected);
`;

export const InlineActions = styled.div<{ $alignRight?: boolean }>`
  display: flex;
  align-items: center;

  ${({ $alignRight }) =>
    $alignRight &&
    css`
      flex: 1;
      justify-content: flex-end;
    `}
`;

export const InfoBox = styled.div<{ width?: string }>`
  display: flex;
  width: ${({ width }) => width || "100%"};
  min-width: 0;
  max-width: 100%;
  height: 65px;
  flex-direction: column;
  align-items: flex-start;
  gap: ${vars.spacing.xs};
`;

export const FieldGroup = styled.div<{
  width?: string;
  height?: string;
  $disabled?: boolean;
}>`
  display: flex;
  width: ${({ width }) => width || "100%"};
  height: auto;
  min-height: 92px;
  margin-bottom: var(--inzeloop-ds-spacing-md);
  flex-shrink: 0;
  flex-direction: column;
  align-items: flex-start;
  border-radius: ${vars.radius.sm};

  ${({ $disabled }) =>
    $disabled &&
    css`
      color: var(--inzeloop-ds-color-text-placeholder-strong);
      pointer-events: none;

      input,
      label {
        color: var(--inzeloop-ds-color-text-placeholder-strong);
        cursor: not-allowed;
      }
    `}
`;

export const PreviewStack = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
  gap: ${vars.spacing.xl};

  @media (min-width: 834px) {
    min-width: 510px;
  }
`;

export const PreviewField = styled.div<{ $showBorder?: boolean }>`
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
  gap: ${vars.spacing.xs};
  padding-bottom: ${vars.spacing.lg};
  border-bottom: ${({ $showBorder = true }) =>
    $showBorder
      ? `${vars.borderWidth.thin} solid ${vars.color.background.surface}`
      : "none"};
`;

export const PreviewValue = styled(FormValue)``;

export const ResponsiveFieldRow = styled.div<{ columns?: number }>`
  display: flex;
  width: 100%;
  align-self: stretch;
  flex-wrap: wrap;
  gap: var(--inzeloop-ds-spacing-29px) ${vars.spacing.xl};

  > * {
    flex: ${({ columns = 1 }) =>
      `1 0 calc((100% - (${columns - 1} * 24px)) / ${columns})`};
  }
`;

export const DetailsGrid = styled.div`
  display: grid;
  width: 100%;
  grid-template-columns: repeat(4, 1fr);
  gap: ${vars.spacing.xl};
`;

export const ProfilePage = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: ${vars.spacing.xl};
`;

export const ProfileGrid = styled.div`
  display: grid;
  width: 100%;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  column-gap: ${vars.spacing.xl};
  row-gap: ${vars.spacing.lg};

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 600px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const ProfileField = styled(FormField)<{
  $column: string;
  $row: string;
}>`
  min-width: 0;
  gap: ${vars.spacing.xs};
  grid-column: ${({ $column }) => $column};
  grid-row: ${({ $row }) => $row};
  padding-bottom: ${vars.spacing.lg};

  @media (max-width: 900px) {
    grid-column: auto;
    grid-row: auto;
  }
`;

export const ProfileValue = styled(FormValue)`
  max-width: 100%;
  overflow-wrap: anywhere;
  white-space: pre-line;
`;

export const DraggableCardStack = styled.div`
  display: flex;
  box-sizing: border-box;
  width: 100%;
  flex-direction: column;
  gap: ${vars.spacing["2xl"]};
`;

export const DraggableCard = styled.div<{ isDragging?: boolean }>`
  width: 100%;
  border: ${vars.borderWidth.thin} solid ${vars.color.border.disabled};
  border-radius: ${vars.radius.lg};
  background: ${({ isDragging }) =>
    isDragging ? vars.color.background.page : vars.color.background.surface};
  padding: ${vars.spacing.lg};
  box-shadow: ${({ isDragging }) => (isDragging ? vars.shadow.drag : "none")};
  cursor: grab;
  user-select: none;
`;

export const ScorecardPage = styled.div`
  display: flex;
  box-sizing: border-box;
  flex-direction: column;
  gap: var(--inzeloop-ds-spacing-30px);
`;

export const FormStack = styled.form`
  display: inline-flex;
  width: 100%;
  box-sizing: border-box;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--inzeloop-ds-spacing-10px);
`;

export const HeaderActionGroup = styled.div<{
  variant?: "gradient" | "transparent" | "danger";
  $stacked?: boolean;
  $alignWithContentEdge?: boolean;
}>`
  display: flex;
  height: 48px;
  flex-shrink: 0;
  gap: ${vars.spacing.lg};
  border: ${vars.borderWidth.thin} solid
    ${({ variant }) =>
      variant === "danger"
        ? vars.color.action.danger
        : vars.color.border.transparent};
  border-radius: ${vars.radius.sm};
  background: ${({ variant }) =>
    variant === "transparent"
      ? `linear-gradient(${vars.color.background.surface}, ${vars.color.background.surface}) padding-box, ${vars.color.action.aiGradient} border-box`
      : vars.color.background.transparent};
  margin: ${({ $stacked, $alignWithContentEdge }) =>
    $stacked || $alignWithContentEdge ? "0" : `0 ${vars.spacing.lg}`};
`;

export const MenuSeparator = styled.div<{ width?: string }>`
  width: ${({ width }) => width || "136px"};
  height: ${vars.borderWidth.thin};
  margin-top: ${vars.spacing.xs};
  margin-bottom: ${vars.spacing.sm};
  background: ${vars.color.border.subtle};
`;

export const FloatingActionBar = styled.div<{ width?: string }>`
  position: sticky;
  bottom: var(--inzeloop-ds-spacing-20px);
  left: auto;
  display: flex;
  width: ${({ width }) => width || "612px"};
  align-self: center;
  align-items: center;
  justify-content: space-between;
  margin-left: 0;
  padding: ${vars.spacing.lg};
  border: ${vars.borderWidth.hairline} solid ${vars.color.border.light};
  border-radius: ${vars.radius.xl};
  background: ${vars.color.background.surface};
  box-shadow: ${vars.shadow.dropdown};
`;

export const ActionBarGroup = styled.div<{ $alignRight?: boolean }>`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  gap: ${({ $alignRight }) =>
    $alignRight ? "var(--inzeloop-ds-spacing-20px)" : vars.spacing.lg};
  margin-left: ${({ $alignRight }) => ($alignRight ? "auto" : "0")};
`;

export const ActionModalContent = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: flex-start;

  ul {
    margin: ${vars.spacing.none};
    padding-left: var(--inzeloop-ds-spacing-20px);
  }
`;

export const WideTitleRow = styled.div`
  display: flex;
  width: 1103px;
  align-items: flex-start;
  gap: var(--inzeloop-ds-spacing-132px);
`;

export const PillGroup = styled.div`
  display: flex;
  align-self: stretch;
  align-items: flex-end;
  gap: ${vars.spacing.lg};
`;

export const PillGroupContent = styled.div`
  display: flex;
  align-items: flex-start;
`;

export const PillGroupItems = styled.div`
  display: flex;
  flex: 1 0 0;
  align-items: center;
  justify-content: center;
  gap: ${vars.spacing.lg};
`;

export const FormSectionTitle = styled.h2`
  margin: 0 0 ${vars.spacing.lg};
  color: ${vars.color.text.muted};
  font-family: ${vars.typography.family.sans};
  font-size: ${vars.typography.size.lg};
  font-weight: ${vars.typography.weight.semibold};
  line-height: ${vars.typography.lineHeight.lg};
`;
